import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnInit,
  ViewChild
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgxSignaturePadComponent } from '@mr-samani/ngx-signature-pad';
import { ToastrService } from 'ngx-toastr';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { API_ROUTES } from 'src/app/core/_constants/api-route.constant';
import { AppConst } from 'src/app/core/_constants/app.constant';
import { MainService } from 'src/app/core/_services/main.service';
import { UploadService } from 'src/app/core/_services/upload.service';

/** Which step of the two-step signing flow we are on. */
type SignatureStep = 'employee' | 'customer';

@Component({
  selector: 'app-approve-estimate',
  templateUrl: './approve-estimate.component.html',
  styleUrls: ['./approve-estimate.component.scss']
})
export class ApproveEstimateComponent implements OnInit, AfterViewInit {

  // ── Signature pad refs ─────────────────────────────────────────────────────
  @ViewChild('employeeSignaturePad') employeeSignaturePad!: NgxSignaturePadComponent;
  @ViewChild('customerSignaturePad') customerSignaturePad!: NgxSignaturePadComponent;

  // ── Container refs (for canvas sizing) ─────────────────────────────────────
  @ViewChild('employeeSignPadContainer') employeeSignPadContainer!: ElementRef<HTMLElement>;
  @ViewChild('customerSignPadContainer') customerSignPadContainer!: ElementRef<HTMLElement>;

  // ── Editable customer name input ────────────────────────────────────────────
  @ViewChild('customerNameInput') customerNameInput!: ElementRef<HTMLInputElement>;

  // ── Public state ────────────────────────────────────────────────────────────
  public dateFormat = AppConst.DATE_FORMAT;
  public today: Date = new Date();

  /** Controls which panel is visible. */
  currentStep: SignatureStep = 'employee';

  /** Legacy flag kept for template compatibility with the items table. */
  initialPage: boolean = true;

  // Signature blank-state flags
  isEmployeeSignatureEmpty: boolean = true;
  isCustomerSignatureEmpty: boolean = true;

  // Uploaded image URLs
  employeeImageUrl: string = '';
  customerImageUrl: string = '';
  employeeSignAt: string = '';
  customerSignAt: string = '';

  // Estimate lists
  uncheckedEstimate: any[] = [];
  checkedEstimate: any[] = [];

  imageIncludeEstimateList: any[] = [];

  // Customer name (editable)
  customerName: string = '';
  editingCustomerName: boolean = false;
  private _originalCustomerName: string = '';

  // Inspection buttons
  inspectionBtn: any;
  adminProfile: any;

  // ── Constructor ─────────────────────────────────────────────────────────────
  constructor(
    public dialogRef: MatDialogRef<ApproveEstimateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private loader: NgxUiLoaderService,
    private toastr: ToastrService,
    private upload: UploadService,
    private service: MainService,
  ) { }

  // ── Lifecycle ───────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.getDefaultStatusButton();
    this.service.currentUser.subscribe(res => {
      this.adminProfile = res;
    })
    // Items not yet approved and not already included
    this.uncheckedEstimate = this.data.allEstimate.filter((x: any) =>
      !this.data.approveEstimate.includes(x._id) && !x.isInclude && !x.noData
    );
    this.imageIncludeEstimateList = this.data.allEstimate.filter((x: any) =>
      x?.images?.length > 0
    );

    // Seed the customer name from injected data (fall back to empty string)
    this.customerName = this.data.customerName || '';
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.resizeActiveCanvas(), 500);
  }

  // ── Canvas sizing ───────────────────────────────────────────────────────────
  private resizeActiveCanvas(): void {
    if (this.currentStep === 'employee') {
      this.resizePad(this.employeeSignPadContainer, this.employeeSignaturePad);
    } else {
      this.resizePad(this.customerSignPadContainer, this.customerSignaturePad);
    }
  }

  private resizePad(container: ElementRef<HTMLElement> | undefined, pad: NgxSignaturePadComponent | undefined): void {
    if (!container || !pad) return;
    const canvas = (pad as any).canvas;
    if (canvas?.nativeElement) {
      canvas.nativeElement.width = container.nativeElement.clientWidth;
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.resizeActiveCanvas();
  }

  // ── Employee signature ──────────────────────────────────────────────────────
  onEmployeeSignature(_ev: string): void {
    this.isEmployeeSignatureEmpty = this.isCanvasBlank((this.employeeSignaturePad as any).ctx);
  }

  clearEmployee(pad: NgxSignaturePadComponent): void {
    pad.clearSignaturePad();
    this.isEmployeeSignatureEmpty = true;
    this.employeeImageUrl = '';
    this.employeeSignAt = '';
  }

  /**
   * Upload the employee signature and – if successful – advance to the
   * customer step.
   */
  async proceedToCustomer(pad: NgxSignaturePadComponent): Promise<void> {
    const file = pad.getSignatureFile();
    const uploaded = await this.uploadSignatureFile(file);
    if (!uploaded) return;
    this.employeeImageUrl = uploaded;
    this.employeeSignAt = new Date().toISOString();

    // Move to customer step and size the new canvas
    this.currentStep = 'customer';
    setTimeout(() => {
      this.resizePad(this.customerSignPadContainer, this.customerSignaturePad);
    }, 500);
  }

  // ── Customer signature ──────────────────────────────────────────────────────
  onCustomerSignature(_ev: string): void {
    this.isCustomerSignatureEmpty = this.isCanvasBlank((this.customerSignaturePad as any).ctx);
  }

  clearCustomer(pad: NgxSignaturePadComponent): void {
    pad.clearSignaturePad();
    this.isCustomerSignatureEmpty = true;
    this.customerImageUrl = '';
    this.customerSignAt = '';
  }

  /**
   * Final step: upload the customer signature then close the dialog with both
   * image URLs and the approved IDs.
   */
  async getFile(pad: NgxSignaturePadComponent): Promise<void> {
    const file = pad.getSignatureFile();
    const uploaded = await this.uploadSignatureFile(file);
    if (!uploaded) return;
    this.customerImageUrl = uploaded;
    this.customerSignAt = new Date().toISOString();

    this.dialogRef.close({
      employeeImageUrl: this.employeeImageUrl,
      employeeSignAt: this.employeeSignAt,
      customerImageUrl: this.customerImageUrl,
      customerSignAt: this.customerSignAt,
      customerName: this.customerName,
      employeeName: this.adminProfile?.fullName,
      approved: [...this.data.approveEstimate, ...this.checkedEstimate]
    });
  }

  // ── Editable customer name ──────────────────────────────────────────────────
  startEditCustomerName(): void {
    this._originalCustomerName = this.customerName;
    this.editingCustomerName = true;
    // Focus the input on next tick (after *ngIf renders it)
    setTimeout(() => this.customerNameInput?.nativeElement.focus(), 0);
  }

  finishEditCustomerName(): void {
    this.editingCustomerName = false;
  }

  cancelEditCustomerName(): void {
    this.customerName = this._originalCustomerName;
    this.editingCustomerName = false;
  }

  // ── Estimate include/exclude ────────────────────────────────────────────────
  estimateChecked(id: any): void {
    const index = this.checkedEstimate.indexOf(id);
    if (index === -1) {
      this.checkedEstimate.push(id);
    } else {
      this.checkedEstimate.splice(index, 1);
    }
  }

  download() {
    this.dialogRef.close({
      approved: [...this.checkedEstimate]
    })
  }

  // ── Amount calculation ──────────────────────────────────────────────────────
  calculateAmount(key: string): number {
    let total = 0;
    this.data.allEstimate.forEach((order: any) => {
      if (
        order._id &&
        (this.data.approveEstimate.includes(order._id) || this.checkedEstimate.includes(order._id))
      ) {
        total += parseFloat(order[key]) || 0;
      }
    });
    return Number(total.toFixed(2));
  }

  // ── Inspection status ───────────────────────────────────────────────────────
  getDefaultStatusButton(): void {
    this.service.get(`${API_ROUTES.SETTING.getInspectionBtnStatus}`, {}).subscribe(response => {
      this.loader.start();
      if (response.status) {
        this.inspectionBtn = response.data?.list;
      }
      this.loader.stop();
    });
  }

  setInspectionStatus(id: number): string {
    const foundBtn = this.inspectionBtn?.find((ele: any) => ele?._id === id);
    if (!foundBtn) return '';
    switch (foundBtn.originalName) {
      case 'Fail': return 'red-btn';
      case 'Pass': return 'pass-status';
      case 'Pending': return 'pend-status';
      case 'N/A': return 'null-status';
      default: return '';
    }
  }

  // ── Private helpers ─────────────────────────────────────────────────────────
  private isCanvasBlank(ctx: any): boolean {
    const pixelBuffer = new Uint32Array(
      ctx.getImageData(0, 0, 1440, 240).data.buffer
    );
    return !pixelBuffer.some(color => color !== 0);
  }

  private async uploadSignatureFile(file: File): Promise<string | null> {
    if (!file) return null;

    if (file.type.match(/png/) == null) {
      this.toastr.error('Only PNG signatures are supported.');
      return null;
    }

    this.loader.start();
    try {
      const url: any = await this.upload.uploadFile(file);
      this.loader.stop();
      if (url) return url;
      this.toastr.error(`${AppConst.ERROR_MESSAGES.SERVER_ERROR}`);
      return null;
    } catch {
      this.loader.stop();
      this.toastr.error(`${AppConst.ERROR_MESSAGES.SERVER_ERROR}`);
      return null;
    }
  }

  goBackToEmployee(): void {
    this.currentStep = 'employee';

    // Customer signature becomes invalid once flow moves backward
    this.customerImageUrl = '';
    this.customerSignAt = '';
    this.isCustomerSignatureEmpty = true;

    if (this.customerSignaturePad) {
      this.customerSignaturePad.clearSignaturePad();
    }

    setTimeout(() => {
      this.resizePad(this.employeeSignPadContainer, this.employeeSignaturePad);
    }, 300);
  }
}